import React, { useState } from 'react';
import type { PlayerHand, HeistDeckId } from '../../../../games/heist-city/heistDecks';
import { HEIST_CARD_BY_ID } from '../../../../games/heist-city/heistDecks';

interface HandViewModalProps {
  playerName: string;
  playerNumber: 1 | 2;
  hand: PlayerHand;
  onPlayCard: (cardId: string) => void;
  onReorderHand: (cards: string[]) => void;
  onClose: () => void;
  readOnly?: boolean;
}

const DECK_STYLE: Record<HeistDeckId, { text: string; bg: string; border: string; badge: string }> = {
  overt:     { text: 'text-red-300',    bg: 'bg-red-950/80',    border: 'border-red-500/50',    badge: 'bg-red-700' },
  hidden:    { text: 'text-purple-300', bg: 'bg-purple-950/80', border: 'border-purple-500/50', badge: 'bg-purple-700' },
  disguised: { text: 'text-green-300',  bg: 'bg-green-950/80',  border: 'border-green-500/50',  badge: 'bg-green-700' },
};

const HandViewModal: React.FC<HandViewModalProps> = ({
  playerName, playerNumber, hand, onPlayCard, onReorderHand, onClose, readOnly,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const teamColor = playerNumber === 1 ? 'text-blue-400' : 'text-red-400';

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedIdx(idx);
  };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };
  const handleDrop = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === toIdx) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }
    const reordered = [...hand.cards];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(toIdx, 0, moved);
    onReorderHand(reordered);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const handlePlay = (cardId: string) => {
    onPlayCard(cardId);
    if (selectedId === cardId) setSelectedId(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col pointer-events-none">
      {/* Backdrop — clickable to close */}
      <div
        className="absolute inset-0 bg-black/60 pointer-events-auto"
        onClick={onClose}
      />

      {/* Bottom panel */}
      <div className="relative mt-auto bg-gray-900 border-t-2 border-gray-700 pointer-events-auto">
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div>
            <span className={`text-base font-bold ${teamColor}`}>{playerName}'s Hand</span>
            <span className="ml-3 text-xs text-gray-400">
              {hand.cards.length} card{hand.cards.length !== 1 ? 's' : ''} in hand
            </span>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors"
          >
            Close
          </button>
        </div>

        {/* Hand cards */}
        <div className="px-4 py-3">
          {hand.cards.length === 0 ? (
            <p className="text-gray-500 italic text-sm py-4 text-center">
              No cards — use "Deal Hand" to draw your opening hand.
            </p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {hand.cards.map((cardId, idx) => {
                const card = HEIST_CARD_BY_ID[cardId];
                if (!card) return null;
                const s = DECK_STYLE[card.deck];
                const isSelected = selectedId === cardId;
                const isDragging = draggedIdx === idx;
                const isDragTarget = dragOverIdx === idx && draggedIdx !== idx;

                return (
                  <div
                    key={cardId}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedId(isSelected ? null : cardId)}
                    className={[
                      'flex-shrink-0 w-44 p-3 rounded-lg border cursor-pointer select-none transition-all',
                      s.bg, s.border,
                      isSelected ? 'ring-2 ring-white -translate-y-2 shadow-lg' : 'hover:-translate-y-1',
                      isDragging ? 'opacity-30' : '',
                      isDragTarget ? 'ring-2 ring-yellow-400 scale-105' : '',
                    ].join(' ')}
                  >
                    <span className={`text-xs px-1.5 py-0.5 rounded font-bold text-white capitalize ${s.badge}`}>
                      {card.deck}
                    </span>
                    <p className={`mt-2 text-sm font-bold ${s.text}`}>{card.name}</p>
                    <p className="mt-1 text-xs text-gray-300 leading-snug">{card.effect}</p>
                    {!readOnly && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePlay(cardId); }}
                        className="mt-3 w-full px-2 py-1 bg-yellow-600 hover:bg-yellow-500 text-white text-xs rounded font-semibold transition-colors"
                      >
                        Play
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Active played card reminder */}
        {hand.activePlayedCard && (() => {
          const card = HEIST_CARD_BY_ID[hand.activePlayedCard];
          if (!card) return null;
          const s = DECK_STYLE[card.deck];
          return (
            <div className="px-4 pb-3 border-t border-gray-800 pt-3 flex items-center gap-2 text-xs text-gray-400">
              <span className="font-semibold">Active played card:</span>
              <span className={`px-1.5 py-0.5 rounded border ${s.bg} ${s.text} ${s.border}`}>{card.name}</span>
              <span className="text-gray-500 italic">{card.effect}</span>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default HandViewModal;
